/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { includes, toLower } from 'lodash'
import { Spinner } from 'react-bootstrap'

import { ReactComponent as IconSearch } from 'assets/icons/search.svg'
import FormControl from 'components/Form/FormControl'
import TouchableOpacity from 'components/TouchableOpacity'
import { getFileLink } from 'utils/helpers/file'
import { openLink } from 'utils/html/window'
import { useResourceLibrary } from 'pages/Coach/ResourceLibrary/hooks'

import styles from './styles.module.scss'

const isRecommendedTo = (resource, userId) =>
  (resource.UserRecommendedResources || []).some(
    r => Number(r.userId) === userId
  )

const resourceLink = resource =>
  resource.type === 'document'
    ? getFileLink(resource.url)
    : resource.url

const ResourceRecommendations = () => {
  const { userId } = useParams()
  const numericUserId = Number(userId)
  const {
    resourceLibraryData,
    getAllResources,
    resourceLibraryLoading,
    recommendResourceToUser
  } = useResourceLibrary()

  const [filterTitle, setFilterTitle] = useState('')
  // Live checkbox state: resource ids recommended to this user.
  const [recommendedIds, setRecommendedIds] = useState(new Set())
  // Frozen "recommended-first" ordering snapshot taken on load, so rows don't
  // jump around as the coach toggles during the session.
  const [orderSnapshot, setOrderSnapshot] = useState(new Set())
  const initedForRef = useRef(null)

  useEffect(() => {
    getAllResources()
  }, [getAllResources])

  const resources = Array.isArray(resourceLibraryData)
    ? resourceLibraryData
    : []

  // Initialize local state once per profile, when data is available.
  useEffect(() => {
    if (initedForRef.current === numericUserId) return
    if (!resources.length) return

    const recommended = new Set(
      resources
        .filter(r => isRecommendedTo(r, numericUserId))
        .map(r => r.id)
    )
    setRecommendedIds(recommended)
    setOrderSnapshot(new Set(recommended))
    initedForRef.current = numericUserId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceLibraryData, numericUserId])

  const handleToggle = resourceId => {
    const nextRecommended = !recommendedIds.has(resourceId)

    setRecommendedIds(prev => {
      const next = new Set(prev)
      if (nextRecommended) next.add(resourceId)
      else next.delete(resourceId)
      return next
    })

    recommendResourceToUser(
      {
        resourceId,
        userId: numericUserId,
        recommended: nextRecommended
      },
      {
        onError: () => {
          // Revert optimistic change on failure.
          setRecommendedIds(prev => {
            const next = new Set(prev)
            if (nextRecommended) next.delete(resourceId)
            else next.add(resourceId)
            return next
          })
        }
      }
    )
  }

  const visibleResources = [...resources]
    .filter(r =>
      filterTitle.length
        ? includes(toLower(r.title || ''), toLower(filterTitle))
        : true
    )
    .sort((a, b) => {
      const aRec = orderSnapshot.has(a.id)
      const bRec = orderSnapshot.has(b.id)
      if (aRec !== bRec) return aRec ? -1 : 1
      return toLower(a.title || '').localeCompare(
        toLower(b.title || '')
      )
    })

  const openResource = resource => e => {
    // Prevent the wrapping <label> from toggling the checkbox.
    e.preventDefault()
    openLink({
      url: resourceLink(resource),
      isExternalLink: true,
      newTab: true
    })
  }

  return (
    <div className={styles.container}>
      <div className="d-flex justify-content-between align-items-center mb-20">
        <div className="font-weight-bold">
          Recommended Content ({recommendedIds.size})
        </div>
        <FormControl
          placeholder="Search content"
          onChange={e => setFilterTitle(e.target.value)}
          formGroupClass={`${styles.search} mb-0`}
          icon={<IconSearch />}
        />
      </div>

      {resourceLibraryLoading && !resources.length ? (
        <div className="d-flex justify-content-center py-40">
          <Spinner animation="border" />
        </div>
      ) : !visibleResources.length ? (
        <div className="text-gray-dark py-20">
          No content found.
        </div>
      ) : (
        <div className={styles.list}>
          {visibleResources.map(resource => (
            <label key={resource.id} className={styles.row}>
              <input
                type="checkbox"
                checked={recommendedIds.has(resource.id)}
                onChange={() => handleToggle(resource.id)}
              />
              <div className={styles.rowBody}>
                <div className={styles.title}>
                  {resource.title}
                </div>
                <div className={styles.meta}>
                  {resource.ResourceCategory?.name}
                  {resource.type ? ` · ${resource.type}` : ''}
                </div>
              </div>
              {resourceLink(resource) ? (
                <TouchableOpacity
                  onClick={openResource(resource)}
                >
                  <span className="link">Open</span>
                </TouchableOpacity>
              ) : null}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default ResourceRecommendations
